import styled, { css } from 'styled-components';

import { colors } from '@/theme/colors';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'danger'
  | 'ghost';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  children?: React.ReactNode;
  text?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disableClick?: boolean; // 클릭 이벤트만 비활성화 (시각적으로는 활성 상태)
}

const Button = ({
  children,
  text,
  variant = 'primary',
  size = 'medium',
  active = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  type = 'button',
}: ButtonProps) => {
  // disabled 상태 계산
  const isFullyDisabled = disabled || loading;

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <StyledButton
      type={type}
      disabled={isFullyDisabled}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $active={active}
      $disabled={isFullyDisabled}
      $loading={loading}
      onClick={handleClick}
    >
      {loading && <LoadingSpinner />}
      <ButtonContent $loading={loading}>{children || text}</ButtonContent>
    </StyledButton>
  );
};

export default Button;

interface StyledButtonProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $active: boolean;
  $disabled: boolean;
  $loading: boolean;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${colors.primaryBg};
        color: ${colors.primaryText};
        border: 1px solid transparent;

        &:hover:not(:disabled) {
          background: ${colors.primaryHover};
          border-color: ${colors.border.focus};
        }
      `;

    case 'secondary':
      return css`
        background: ${colors.secondaryBg};
        color: ${colors.secondaryText};
        border: 1px solid ${colors.secondaryBorder};

        &:hover:not(:disabled) {
          background: ${colors.secondaryHover};
          border-color: ${colors.border.focus};
        }

        &:focus:not(:disabled) {
          outline: none;
        }
      `;

    case 'info':
      return css`
        background: ${colors.infoBg};
        color: ${colors.infoText};

        &:hover:not(:disabled) {
          background: ${colors.infoHover};
        }
        &:focus:not(:disabled) {
          outline: none;
        }
      `;

    case 'danger':
      return css`
        background: ${colors.error};
        color: ${colors.white};
        border: 1px solid transparent;
      `;

    case 'ghost':
      return css`
        background: transparent;
        color: ${colors.black};
        border: 1px solid transparent;
      `;

    default:
      return '';
  }
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: 4px 12px;
        font-size: 12px;
        min-height: 28px;
        border-radius: 16px;
      `;

    case 'medium':
      return css`
        padding: 6px 24px;
        font-size: 14px;
        min-height: 36px;
        border-radius: 32px;
      `;

    case 'large':
      return css`
        padding: 12px 32px;
        font-size: 16px;
        min-height: 48px;
        border-radius: 32px;
      `;

    default:
      return '';
  }
};

const getActiveStyles = (active: boolean) => {
  if (active) {
    return css`
      background: ${colors.disabled} !important;
    `;
  }
};

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  position: relative;
  white-space: nowrap;
  user-select: none;

  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $size }) => getSizeStyles($size)}
  ${({ $active }) => getActiveStyles($active)}
  
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  // 커서 상태 관리
  ${({ $disabled, $loading }) => {
    if ($disabled) return 'cursor: not-allowed;';
    if ($loading) return 'cursor: wait;';
    return 'cursor: pointer;';
  }}

  // 완전 비활성화 상태 (시각적 변경)
  ${({ $disabled }) =>
    $disabled &&
    css`
      background: ${colors.disabled} !important;
      color: ${colors.white} !important;
      box-shadow: none !important;
      transform: none !important;
      border-color: transparent !important;
      opacity: 0.6;
    `}
`;

const ButtonContent = styled.span<{ $loading: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
